// Catalogue loader. Fetches categories/brands/products/inventory once (in
// parallel) and derives the lookup maps the storefront needs, cached at module
// scope so navigating between the shop grid and a product page never refetches.

import { useCallback, useEffect, useState } from 'react'
import { listProducts, listCategories, listBrands, listInventory } from './shopApi'
import type { Product, Category, Brand, InventoryItem } from './types'

export interface Catalogue {
  products: Product[]
  categories: Category[]
  brands: Brand[]
  inventory: InventoryItem[]
  /** InventoryItem.available, keyed by SKU. */
  stockBySku: Record<string, number>
  categoryById: Record<string, Category>
  brandById: Record<string, Brand>
}

const EMPTY: Catalogue = {
  products: [],
  categories: [],
  brands: [],
  inventory: [],
  stockBySku: {},
  categoryById: {},
  brandById: {},
}

let cache: Catalogue | null = null
let inflight: Promise<Catalogue> | null = null

function derive(
  products: Product[],
  categories: Category[],
  brands: Brand[],
  inventory: InventoryItem[],
): Catalogue {
  return {
    products,
    categories,
    brands,
    inventory,
    stockBySku: Object.fromEntries(inventory.map((i) => [i.sku, i.available])),
    categoryById: Object.fromEntries(categories.map((c) => [c.id, c])),
    brandById: Object.fromEntries(brands.map((b) => [b.id, b])),
  }
}

async function load(): Promise<Catalogue> {
  const [products, categories, brands, inventory] = await Promise.all([
    listProducts(),
    listCategories(),
    listBrands(),
    listInventory(),
  ])
  cache = derive(products, categories, brands, inventory)
  return cache
}

function ensureLoad(force: boolean): Promise<Catalogue> {
  // Defensive: useCatalogue's effect already returns early on a warm cache, so
  // no current caller reaches this with `cache && !force`. Kept for any future
  // caller; excluded from coverage because no test can reach it through the hook.
  /* v8 ignore next */
  if (cache && !force) return Promise.resolve(cache)
  if (!inflight || force) {
    inflight = load().finally(() => {
      inflight = null
    })
  }
  return inflight
}

/** Force the next `useCatalogue()` call to refetch (e.g. after stock changes). */
export function invalidateCatalogue() {
  cache = null
  inflight = null
}

export function useCatalogue() {
  const [data, setData] = useState<Catalogue>(cache ?? EMPTY)
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>(cache ? 'ready' : 'loading')
  const [reloadNonce, setReloadNonce] = useState(0)

  const reload = useCallback(() => {
    cache = null
    setPhase('loading')
    setReloadNonce((n) => n + 1)
  }, [])

  useEffect(() => {
    if (cache && reloadNonce === 0) return
    let cancelled = false
    ensureLoad(reloadNonce > 0)
      .then((c) => {
        if (cancelled) return
        setData(c)
        setPhase('ready')
      })
      .catch(() => {
        if (!cancelled) setPhase('error')
      })
    return () => {
      cancelled = true
    }
  }, [reloadNonce])

  return { ...data, phase, reload }
}
